// frontend/src/hooks/useCalendarData.js
//
// Read + write hooks for a Calendar and its override children
// (recurring annual holidays, one-off date exceptions).
//
// Cache layout — one entry per resource type per calendar:
//
//   calendars.all(storeId)                    — calendar list (hooks/useReferenceData)
//   calendarHolidays.byCalendar(s, calId)     — every holiday for one calendar
//   calendarExceptions.byCalendar(s, calId)   — every exception for one calendar
//
// Holidays + exceptions are small, so the entire list per calendar lives in
// one cache entry. The CalendarDaysView
// month grid renders by resolving locally against these two lists +
// the calendar's working-days pattern — no per-month server round-trip.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  calendarHolidays as holidaysKey,
  calendarExceptions as exceptionsKey,
} from '../queries/keys';
import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';

const CAL_API = '/api/calendars/';
const HOL_API = '/api/calendar-holidays/';
const EXC_API = '/api/calendar-exceptions/';

const FIVE_MIN = 1000 * 60 * 5;


// ──────────────────────────────────────────────────────────────────
// Reads
// ──────────────────────────────────────────────────────────────────

function extractList(data) {
  return Array.isArray(data) ? data : (data?.results || []);
}

export function useCalendarHolidays(calendarId) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: holidaysKey.byCalendar(storeId, calendarId),
    queryFn: () => apiJson.get(`${HOL_API}?calendar_id=${calendarId}`).then(extractList),
    enabled: !!storeId && !!calendarId,
    staleTime: FIVE_MIN,
  });
  return {
    holidays: query.data ?? [],
    loading: query.isFetching,
    initialLoad: query.isPending,
    isError: query.isError,
    rawError: query.error,
    refetch: query.refetch,
  };
}

export function useCalendarExceptions(calendarId) {
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const query = useQuery({
    queryKey: exceptionsKey.byCalendar(storeId, calendarId),
    queryFn: () => apiJson.get(`${EXC_API}?calendar_id=${calendarId}`).then(extractList),
    enabled: !!storeId && !!calendarId,
    staleTime: FIVE_MIN,
  });
  return {
    exceptions: query.data ?? [],
    loading: query.isFetching,
    initialLoad: query.isPending,
    isError: query.isError,
    rawError: query.error,
    refetch: query.refetch,
  };
}


// ──────────────────────────────────────────────────────────────────
// Calendar mutations
// ──────────────────────────────────────────────────────────────────

export function useCreateCalendar() {
  return useApiMutation({
    mutationFn: (payload) => apiJson.post(CAL_API, payload),
    changes: [DATA.calendars],
  });
}

export function useUpdateCalendar() {
  return useApiMutation({
    mutationFn: ({ id, payload }) => apiJson.put(`${CAL_API}${id}/`, payload),
    changes: [DATA.calendars],
  });
}


// ──────────────────────────────────────────────────────────────────
// Holiday mutations
// ──────────────────────────────────────────────────────────────────

export function useCreateCalendarHoliday(calendarId) {
  return useApiMutation({
    mutationFn: ({ month, day, holidayName }) => apiJson.post(HOL_API, {
      calendar_id: calendarId,
      month,
      day,
      holiday_name: holidayName ?? '',
    }),
    changes: [DATA.calendars],
  });
}

export function useDeleteCalendarHoliday() {
  return useApiMutation({
    mutationFn: ({ id }) => apiJson.delete(`${HOL_API}${id}/`),
    changes: [DATA.calendars],
  });
}


// ──────────────────────────────────────────────────────────────────
// Exception mutations
// ──────────────────────────────────────────────────────────────────

export function useCreateCalendarException(calendarId) {
  return useApiMutation({
    mutationFn: ({ exceptionDate, exceptionName }) => apiJson.post(EXC_API, {
      calendar_id: calendarId,
      exception_date: exceptionDate,
      exception_name: exceptionName ?? '',
    }),
    changes: [DATA.calendars],
  });
}

export function useUpdateCalendarException() {
  return useApiMutation({
    mutationFn: ({ id, exceptionDate, exceptionName }) => apiJson.patch(
      `${EXC_API}${id}/`,
      { exception_date: exceptionDate, exception_name: exceptionName ?? '' },
    ),
    changes: [DATA.calendars],
  });
}

export function useDeleteCalendarException() {
  return useApiMutation({
    mutationFn: ({ id }) => apiJson.delete(`${EXC_API}${id}/`),
    changes: [DATA.calendars],
  });
}
